// SSR content depth for the name generator page. Plain data — rendered by
// NicheNameIdeas.js / NamingTips.js / Faq.js / HowTo.js.
export const nicheSections = [
  {
    id: 'adventure',
    title: 'Adventure Travel Blog Names',
    intro:
      'Grit beats grandeur here. “Epic” and “extreme” have been worn smooth by overuse, but switchback, scree, and river spray still put a reader somewhere real. One trap we see constantly: a name welded to a single sport. The climbing pun reads oddly above your first kayaking post, so pick words that can follow you off the wall and onto the water.',
    names: [
      'Crag Compass',
      'Switchback Stories',
      'Summit Static',
      'Wildpass Journal',
      'Ridgebound Routes',
      'Canyon Kindred',
      'Dusty Ascent',
      'Alpine Detours',
      'Rapids and Ridges',
      'Fernline Expeditions',
      'Boulder Beacon',
      'Steep Side Stories',
      'Trailhead Theory',
      'Granite Footprints',
      'Roaming Ridgeline',
      'Pineward Passage',
    ],
  },
  {
    id: 'couples',
    title: 'Travel Couple Blog Names',
    intro:
      'There is a fine line between “blog written by two people” and “wedding hashtag.” Tandem, paired, and for-two language stays on the right side of it — and leaves room for both of you to write honestly, including about the day one of you booked the wrong airport. Think twice before using surnames; one of you may want a byline of your own someday.',
    names: [
      'Two Tickets Out',
      'Paired Passports',
      'Roaming in Tandem',
      'Shared Mile Stories',
      'Compass for Two',
      'Suitcase Sweethearts',
      'Doubleback Diaries',
      'Together Elsewhere',
      'Pair on Passage',
      'Coasting as Two',
      'Two Seat Stories',
      'Joined Horizon Journal',
      'Duet Detours',
      'Shared Horizon Notes',
      'Matched Mapmakers',
      'Miles Between Us',
    ],
  },
  {
    id: 'solo-female-travel',
    title: 'Solo Female Travel Blog Names',
    intro:
      'Half the solo female travel blogs out there promise fearlessness, which mostly proves how afraid everyone is of sounding afraid. A first-person name works harder than a boast: it puts your point of view in the title and lets the writing supply the confidence. Make sure it also fits your quieter pieces — the museum guides and practical safety posts are usually the ones readers actually search for.',
    names: [
      'Her Open Atlas',
      'She Takes Switchbacks',
      'Wildhearted Wayfinder',
      'Her Farther Shore',
      'Solo Sagebrush',
      'Roaming Her Way',
      'She Goes Light',
      'Her Compass Notes',
      'One Woman Roams',
      'Her Mile Marker',
      'Wayward by Choice',
      'She Maps Morning',
      'Independent Isles',
      'Her Route Forward',
      'Unaccompanied and Unhurried',
      'She Finds North',
    ],
  },
  {
    id: 'family-travel',
    title: 'Family Travel Blog Names',
    intro:
      'The details parents recognize — snacks, car seats, the bedtime that happens in the wrong time zone — age far better than a headcount. “Party of Five” has a built-in expiry date, and “with toddlers” stops being true faster than you would think. Name the family, not the phase: kids outgrow a blog name quicker than a domain registration lapses.',
    names: [
      'Little Boots Abroad',
      'The Roaming Nest',
      'Stroller to Summit',
      'Pack of Passports',
      'Miles with Minis',
      'Compass Crew Notes',
      'Small Steps Elsewhere',
      'Kinfolk on Wheels',
      'Suitcases and Snacks',
      'Tiny Trail Tribe',
      'Family Mapmakers',
      'Rambunctious Routes',
      'Roaming Roots Club',
      'All Ages Away',
      'The Curious Caravan',
      'Bedtimes Beyond Borders',
    ],
  },
  {
    id: 'budget-backpacking',
    title: 'Budget & Backpacking Blog Names',
    intro:
      'The note to hit is resourceful — never cheap, never apologetic. Shoestrings, ledgers, and pack weight tell readers you will show them how the numbers actually work. Keep the numbers out of the name itself, though: a fixed daily budget is a promise inflation will break for you, one country at a time.',
    names: [
      'Shoestring Switchbacks',
      'Frugal Farther',
      'Hostel Pocket Notes',
      'Small Spend Roams',
      'Thrifty Trailcraft',
      'Pack Light Ledger',
      'Daily Fare Detours',
      'Backpack on Balance',
      'Lean Fare Rambler',
      'Spare Change Trails',
      'Wallet Wise Wanderer',
      'Budget Borderlines',
      'Value Route Journal',
      'Bunkbed Bound',
      'Carryon Cents',
      'Lean Miles Log',
    ],
  },
  {
    id: 'food-travel',
    title: 'Food & Travel Blog Names',
    intro:
      '“Foodie” has been ground down to nothing; a market stall at 7am says far more. The strongest names in this niche tie eating to place — a side street, a border crossing, the plate itself. Match the register to your real material, too. A white-tablecloth name sits awkwardly on a blog whose best stories keep happening at bakery counters.',
    names: [
      'Roaming Tasting Spoon',
      'Forkful of Elsewhere',
      'Market Bite Miles',
      'Plates Past Borders',
      'Saffron Side Streets',
      'Street Stall Stories',
      'Palate on Passage',
      'Crumbs Across Maps',
      'Local Plate Ledger',
      'Hungry Mile Journal',
      'Bowl Beyond Borders',
      'Tastes Take Flight',
      'Menu Map Notes',
      'Sip Savor Stroll',
      'The Roaming Ladle',
      'Bites Between Stops',
    ],
  },
  {
    id: 'travel-photography',
    title: 'Travel Photography Blog Names',
    intro:
      'The craft hands you better words than most niches get — aperture, grain, frame, light — and any of them beats another lens pun once you pair it with something that moves. Two things do not belong in the title: your camera brand, because you will switch, and this year’s editing look, because it will date. The name has to outlast both your gear and your presets.',
    names: [
      'Shuttered Switchbacks',
      'Lens Beyond Lines',
      'Aperture Atlas',
      'Framed Faraways',
      'Roaming Focal Point',
      'Light Chaser Routes',
      'Postcard Exposure',
      'Miles in Focus',
      'Viewfinder Footprints',
      'Golden Hourbound',
      'Scenic Grain Stories',
      'Camera Trail Notes',
      'Latitude in Frames',
      'Clicked Compass',
      'Stillness and Miles',
      'Focused Elsewhere',
    ],
  },
];

export const namingTips = [
  {
    id: 'check-domain-options',
    title: 'Check the domain before committing',
    body:
      'The domain is part of the name, not an errand for later. Check the .com first — it is still what people type when they half-remember you — but if it is taken, do not rescue it with a hyphen, a number, or a bolted-on “the.” Those fixes read as compromises to everyone except the person who made them. A clean .blog or .travel beats a mangled .com every time. And an open domain proves nothing about trademarks; that is a separate check.',
  },
  {
    id: 'say-and-spell-it',
    title: 'Make it easy to repeat',
    body:
      'Say the name to someone who has never seen it written, then ask them to spell it back. If “Roam Rite” comes back as “Rome Right,” the wordplay is quietly costing you readers. Blogs still spread by mouth — a podcast mention, a hostel kitchen, a friend texting “you’d like this site” — and every spelling correction loses a few people halfway through typing. One unusual word is fine when the rest of the name carries it. Two turns your brand into a spelling test.',
  },
  {
    id: 'leave-dates-behind',
    title: 'Leave dates and ages out',
    body:
      '“Backpacking at Thirty” has a birthday problem. “Europe Summer 2026” sounds like a trip that already ended. A year or an age keeps a blog feeling current for about one season, and after that you are either renaming or explaining. Dates do belong in article titles, where freshness genuinely helps readers — just keep the masthead off the calendar, so your old guides can be updated instead of abandoned.',
  },
  {
    id: 'allow-room-to-change',
    title: 'Give your travels room to change',
    body:
      'Name the thread that runs through your writing, not this year’s itinerary. “Hostels of Iberia” is wonderfully precise right up until you take a train across Japan or rent a flat for a month. What lasts is the way you travel — the curiosity, the slow mornings, the careful budgets — not the vehicle, the route, or the relationship status. Be specific about how you see, and stay vague about where you happen to be.',
  },
  {
    id: 'match-social-handles',
    title: 'Keep social handles recognizable',
    body:
      'Check handles on the platforms you will actually use, not all nine. An exact match is nice; a consistent modifier is nearly as good — “stories” added everywhere is easier to remember than “blog” on one network, a country code on a second, and an underscore on the third. But never let one squatted handle sink a strong domain. The website is the permanent address. Platforms change their rules, their reach, and occasionally their entire existence.',
  },
  {
    id: 'check-existing-brands',
    title: 'Look for existing brand conflicts',
    body:
      'Search the exact phrase in quotes, then the words reversed, then the obvious misspellings. Cast wider than blogs: a tour operator, a luggage label, a podcast, or a dormant-but-registered company can all make a “free” name legally awkward. If the blog is headed toward real income, check the trademark registers where you plan to operate — an hour of professional advice costs less than a rebrand. You are not hunting an unused URL; you are choosing something readers will attach to you and nobody else.',
  },
  {
    id: 'skip-crowded-cliches',
    title: 'Move past crowded travel words',
    body:
      'Wanderlust, nomad, globetrotter, vagabond: each one instantly says travel, appears in a few thousand existing blog names, and disappears into a page of search results. If one genuinely fits, anchor it to something personal and concrete. Better still, build from your own material — the night train you keep taking, the market breakfasts, the coast road you cannot stop going back to. Nobody else’s blog is built from your notebook, and that is what makes a name findable.',
  },
];

export const faqs = [
  {
    questionName: 'How can I come up with a creative name for my travel blog?',
    acceptedAnswerText:
      'Start from your raw material: how you travel, what you notice, the places you keep going back to. Write down concrete nouns — ferry, ridge, market, notebook — and pair them with motion or personality words until a phrase sounds natural said aloud. Names built from a specific detail tend to stick; names stacked from broad words like travel and adventure tend to blur into everyone else’s.',
  },
  {
    questionName: 'What should I avoid when choosing a travel blog name?',
    acceptedAnswerText:
      'Avoid names that are long, hard to spell, dependent on a hyphen or number, or one letter away from an existing brand. Dates and ages expire, a single destination boxes you in, and a joke that needs explaining stops being funny around the tenth explanation. Before committing, search the exact phrase, check the domain and the handles you care about, and look at trademark records for anything close.',
  },
  {
    questionName: 'What makes a good travel blog name?',
    acceptedAnswerText:
      'A good travel blog name is easy to say, easy to spell, distinctive in a page of search results, and roomy enough for the stories you have not written yet. It should suggest your angle without cataloguing your topics. Concrete imagery does the heavy lifting — switchback, night train, market — because it gives readers something to picture and remember. It should also survive being read aloud as a domain.',
  },
  {
    questionName: 'How long should a travel blog name be?',
    acceptedAnswerText:
      'Two or three words is the sweet spot: enough room for an idea, short enough to survive a logo, a social handle, and a domain typed on a phone. Four words can work when every one earns its place and the phrase flows. Whatever the length, test it with the spaces removed, because that is how it will live in an address bar. The real limit is not characters — it is whether someone can hear the name once and type it correctly.',
  },
  {
    questionName: 'What is the best top-level domain for a travel blog?',
    acceptedAnswerText:
      'Check the .com first; it is still what most readers assume and type from memory. If it is gone, a short .blog or .travel is a legitimate choice — and usually a better one than a .com forced with hyphens, numbers, or a filler word. Say the full address aloud before deciding. No extension rescues a confusing name; clarity does the work, the ending just delivers it.',
  },
  {
    questionName: 'Is the travel blog name generator free?',
    acceptedAnswerText:
      'Yes — TravelFeed’s travel blog name generator is free. Enter a few keywords and it combines them into candidate names; run as many rounds as you like and keep a shortlist. Opening the claim dialog on a candidate checks the matching .com domain, TravelFeed username, and Hive username in one step. Availability changes quickly, so register the domain and accounts you want once you have decided.',
  },
  {
    questionName: 'Should I use my own name for my travel blog?',
    acceptedAnswerText:
      'Your own name works well when the blog is built on your voice and you are comfortable being publicly findable — and it never boxes in your destinations or travel style. A descriptive brand makes more sense if other people might write with you, if you may sell the site one day, or if your name is a spelling hazard. Write both on paper — your name plus a travel cue, and a stand-alone brand — and ask which one you would rather say at a dinner table.',
  },
  {
    questionName: 'Should the word travel be in my blog name?',
    acceptedAnswerText:
      'Not necessarily. The word travel makes the topic instantly clear, but it also makes a name more generic and the matching .com much harder to find. Routes, miles, maps, ferries, trails, and stays signal the subject just as clearly and with more character. Use travel if it lands in a genuinely clean phrase; otherwise let the imagery and a short tagline carry the meaning.',
  },
  {
    questionName:
      'What should I do if the .com domain for my blog name is taken?',
    acceptedAnswerText:
      'Visit the domain first. If an active brand lives there, walk away — sharing a name with an established site creates confusion you cannot outrun. If it is parked or dormant, a clean .blog or .travel is a reasonable alternative, or adjust the name itself. What does not work: hyphens, numbers, misspellings, or a filler word grafted on purely to win the .com. Say the survivors aloud and re-check handles and trademark records before you commit.',
  },
];

export const howToSteps = [
  {
    name: 'Collect useful keywords',
    text:
      'Gather concrete words from your niche, your travel style, and the details that keep showing up in your stories, then feed the strongest ones into the generator. Specific beats broad: night train and tide pool will produce better names than travel and adventure.',
  },
  {
    name: 'Generate and shortlist',
    text:
      'Run a few rounds and keep five to ten candidates that are easy to say and spell. Read each one as a bare domain with the spaces removed, and cut anything confusing, too narrow, or uncomfortably close to a brand you find in search.',
  },
  {
    name: 'Check and claim',
    text:
      'Open the claim dialog on your favorite to check the .com domain, TravelFeed username, and Hive username in one step. When the checks come back clear and a quick brand search turns up no conflicts, register what you want before you start publishing under the name.',
  },
];
